import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, Typography, TextField, Button, Alert, CircularProgress, Paper, IconButton, Tooltip, Divider, Collapse, Table, TableBody, TableCell, TableHead, TableRow, TableContainer } from '@mui/material';
import { testChat, getAis } from '../services/api';
import ReactMarkdown from 'react-markdown';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

// Message interface for conversation
interface ConversationMessage {
  id: string;
  question: string;
  response: string;
  timestamp: Date;
  instruction?: string;
  dbRelevantPairs?: any[];
  nonDbRelevantPairs?: any[];
  queryResult?: any[];
  queryToRun?: string;
  // New retry-related fields
  retryUsed?: boolean;
  originalFailedQuery?: string;
  retryQueryUsed?: string;
  querySequence?: Array<{
    query: string;
    results: number | null;
    type: 'original' | 'retry' | 'single' | 'original_ai' | 'executed' | 'fallback';
    description?: string;
  }>;
  queryAttempted?: boolean;
}

// Component to render input section - moved outside to prevent re-creation
const InputSection = memo(React.forwardRef<HTMLDivElement, {
  questionValue: string;
  onQuestionChange: (value: string) => void;
  onSubmit: () => void;
  isLoading: boolean;
  placeholder: string;
}>(({ questionValue, onQuestionChange, onSubmit, isLoading, placeholder }, ref) => (
  <Box ref={ref} sx={{ mt: 4, mb: 4 }}>
    <TextField
      label={placeholder}
      value={questionValue}
      onChange={(e: any) => onQuestionChange(e.target.value)}
      fullWidth
      sx={{ mb: 2 }}
      disabled={isLoading}
      onKeyPress={(e: any) => {
        if (e.key === 'Enter' && !e.shiftKey && !isLoading) {
          e.preventDefault();
          console.log('Enter key pressed, calling onSubmit');
          onSubmit();
        }
      }}
    />
    
    <Button 
      variant="contained" 
      onClick={() => {
        console.log('Test button clicked for placeholder:', placeholder, 'value:', questionValue);
        onSubmit();
      }}
      disabled={isLoading || !questionValue.trim()}
    >
      {isLoading ? <CircularProgress size={24} /> : 'Test'}
    </Button>
  </Box>
)));

// CollapsibleSection component for debug info
const CollapsibleSection = memo(({ 
  title, 
  count, 
  isExpanded, 
  onToggle, 
  children 
}: {
  title: string;
  count?: number;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) => (
  <Box sx={{ mt: 4 }}>
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        cursor: 'pointer',
        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
        p: 1,
        borderRadius: 1
      }}
      onClick={onToggle}
    >
      <Typography variant="h6" color='blue' sx={{ flexGrow: 1 }}>
        {title} {count !== undefined && `(${count})`}
      </Typography>
      <IconButton size="small">
        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </IconButton>
    </Box>
    <Collapse in={isExpanded}>
      <Box sx={{ mt: 1, pl: 2 }}>
        {children}
      </Box>
    </Collapse>
  </Box>
));

const TestChatbot = () => {
  const { aiId } = useParams<{ aiId: string }>();
  const [searchParams] = useSearchParams();
  const collectionId = searchParams.get('collectionId');

  const [currentQuestion, setCurrentQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiTitle, setAiTitle] = useState('');
  const [versionName, setVersionName] = useState('');
  //test


  // Conversation state - array of messages
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [sessionId] = useState(() => `test-${Date.now()}`);
  
  // State for additional question inputs - one for each message
  const [additionalQuestions, setAdditionalQuestions] = useState<{ [messageId: string]: string }>({});
  
  // Debug mode is always enabled
  const debugMode = true;
  
  // State for collapsible debug sections - tracks if debug info is expanded for each message
  const [debugExpanded, setDebugExpanded] = useState<{
    [messageId: string]: boolean;
  }>({});
  
  // State for expanded table display - tracks if full table is shown for each message
  const [tableExpanded, setTableExpanded] = useState<{
    [messageId: string]: boolean;
  }>({});
  
  // Refs for each message's input section for time travel scrolling
  const messageInputRefs = useRef<{ [messageId: string]: HTMLDivElement | null }>({});
  // Ref to access current messages in callbacks
  const messagesRef = useRef<ConversationMessage[]>([]);

  // Update messagesRef whenever messages change
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Function to scroll to a specific message's input section
  const scrollToMessageInput = (messageId: string) => {
    console.log('scrollToMessageInput called for:', messageId);
    const element = messageInputRefs.current[messageId];
    console.log('Found element:', element);
    if (element) {
      console.log('Scrolling to element...');
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      console.log('Element not found in refs');
    }
  };

  // Memoized callback for handling additional question changes
  const handleAdditionalQuestionChange = useCallback((messageId: string, value: string) => {
    setAdditionalQuestions(prev => ({ ...prev, [messageId]: value }));
  }, []);

  // Memoized callback for submitting additional questions
  const handleAdditionalQuestionSubmit = useCallback((messageId: string) => {
    console.log('handleAdditionalQuestionSubmit called for messageId:', messageId);
    const questionToSend = additionalQuestions[messageId];
    console.log('Question to send:', questionToSend);
    
    if (questionToSend?.trim()) {
      // Use ref to get current messages to avoid stale closure
      const currentMessages = messagesRef.current;
      console.log('Current messages length:', currentMessages.length);
      
      // Find the index of this message in the messages array
      const messageIndex = currentMessages.findIndex(msg => msg.id === messageId);
      console.log('Submitting question for message at index:', messageIndex, 'with question:', questionToSend);
      
      if (messageIndex !== -1) {
        // Cut off conversation after this message and start new branch
        const messagesUpToThisPoint = currentMessages.slice(0, messageIndex + 1);
        console.log('Messages before branch:', currentMessages.length);
        console.log('Messages after branch:', messagesUpToThisPoint.length);
        setMessages(messagesUpToThisPoint);
        
        // Clear additional questions for removed messages
        const removedMessageIds = currentMessages.slice(messageIndex + 1).map(msg => msg.id);
        console.log('Removing additional questions for:', removedMessageIds);
        setAdditionalQuestions(prev => {
          const updated = { ...prev };
          removedMessageIds.forEach(id => delete updated[id]);
          // Also clear the current input
          updated[messageId] = '';
          return updated;
        });
        
        // Send the new question with auto-scroll
        handleTest(questionToSend, true);
      } else {
        console.log('Message not found in current messages array');
      }
    } else {
      console.log('No question to send or question is empty');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalQuestions]); // Removed messages dependency

  // Load AI and collection details when component mounts
  useEffect(() => {
    if (aiId && collectionId) {
      getAis()
        .then((res) => {
          const ai = res.data.find((ai: any) => ai.id === aiId);
          if (ai) {
            setAiTitle(ai.title);
            const collection = ai.collections.find((c: any) => c._id === collectionId);
            if (collection) {
              setVersionName(collection.version);
            }
          }
        })
        .catch((err) => {
          console.error('Error fetching AI details:', err);
          setError('Failed to load AI details');
        });
    }
  }, [aiId, collectionId]);

  const handleTest = (questionToSend: string = currentQuestion, isFromAdditionalInput: boolean = false) => {
    if (!aiId || !collectionId || !questionToSend.trim()) {
      setError('Please enter a question');
      return;
    }

    setLoading(true);
    setError(null);
    
    console.log('handleTest called with:', { questionToSend, isFromAdditionalInput, currentMessagesCount: messages.length });
    
    // If this is from the main input (not additional input) and we have existing messages,
    // reset the conversation to start fresh
    const conversationMessages = isFromAdditionalInput ? messages : [];
    console.log('Using conversation messages:', conversationMessages.length, 'messages for context');
    
    // Create conversation history in the format expected by the backend
    // Use current messages state (which may have been truncated by branching)
    const conversationHistory = conversationMessages.flatMap(msg => [
      {
        from: 'user',
        content: msg.question,
        timestamp: msg.timestamp.getTime()
      },
      {
        from: 'operator',
        content: msg.response,
        timestamp: msg.timestamp.getTime() + 1000 // Add small delay for ordering
      }
    ]).sort((a, b) => a.timestamp - b.timestamp);
    
    console.log('Sending conversation history with', conversationHistory.length / 2, 'message pairs');
    
    testChat(aiId, collectionId, questionToSend, sessionId, conversationHistory)
      .then((res) => {
        // Create new message object
        const newMessage: ConversationMessage = {
          id: `msg-${Date.now()}-${Math.random().toString(36).substring(2)}`,
          question: questionToSend,
          response: res.data.response,
          timestamp: new Date(),
          instruction: res.data.debug?.instruction,
          dbRelevantPairs: res.data.debug?.dbRelevantPairs || [],
          nonDbRelevantPairs: res.data.debug?.nonDbRelevantPairs || [],
          queryResult: res.data.debug?.queryResult || [],
          queryToRun: res.data.debug?.queryToRun,
          // Extract new retry-related fields
          retryUsed: res.data.debug?.retryUsed,
          originalFailedQuery: res.data.debug?.originalFailedQuery,
          retryQueryUsed: res.data.debug?.retryQueryUsed,
          querySequence: res.data.debug?.querySequence,
          queryAttempted: res.data.debug?.queryAttempted
        };

        // If this is from the main input, reset messages array, otherwise append
        if (isFromAdditionalInput) {
          // Add to existing messages array
          console.log('Adding message to existing conversation');
          setMessages(prev => [...prev, newMessage]);
        } else {
          // Reset conversation with just this new message
          console.log('Starting fresh conversation');
          setMessages([newMessage]);
          // Clear all additional questions when starting fresh
          setAdditionalQuestions({});
        }
        
        // Clear the input field for next question only if it's the current question
        if (questionToSend === currentQuestion) {
          setCurrentQuestion('');
        }
        
        // Only auto-scroll if this was from an additional input, not the main input
        if (isFromAdditionalInput) {
          // Small delay to ensure the DOM has updated, then scroll to the new input
          setTimeout(() => {
            scrollToMessageInput(newMessage.id);
          }, 200);
        }
        
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error testing chatbot:', err);
        setError('Error: ' + (err.response?.data?.error || err.message));
        setLoading(false);
      });
  };

  // Helper function to display source tag
  const getSourceDisplay = (pair: any) => {
    if (!pair.tags || pair.tags.length === 0) {
      return "Source: Manual";
    }
    
    // Use the first tag as the source (typically a URL or filename)
    const sourceTag = pair.tags[0];
    
    // Check if it's a URL
    if (sourceTag.startsWith('http://') || sourceTag.startsWith('https://')) {
      return `Source: ${new URL(sourceTag).hostname}`;
    }
    
    return `Source: ${sourceTag}`;
  };

  // Helper function to copy text to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      console.log('Text copied to clipboard');
    }).catch((err) => {
      console.error('Failed to copy text: ', err);
    });
  };

  // Helper function to truncate text to 200 characters
  const truncateText = (text: string, maxLength: number = 200) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Helper function to truncate cell content for table display
  const truncateCellContent = (value: any, maxLength: number = 100) => {
    if (value === null || value === undefined) return '';
    
    let stringValue: string;
    if (typeof value === 'object') {
      stringValue = JSON.stringify(value);
    } else if (typeof value === 'string' && (value.startsWith('<') || value.includes('</'))) {
      // For HTML content, just show truncated plain text
      stringValue = value.replace(/<[^>]*>/g, ''); // Strip HTML tags
    } else {
      stringValue = String(value);
    }
    
    if (stringValue.length <= maxLength) return stringValue;
    return stringValue.substring(0, maxLength) + '...';
  };

  // Helper function to toggle table expansion for a message
  const toggleTableExpansion = (messageId: string) => {
    setTableExpanded(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  // Helper function to toggle debug info visibility for a message
  const toggleDebugInfo = (messageId: string) => {
    setDebugExpanded(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  // Component to render Q&A pairs for a message
  const renderQAPairs = (message: ConversationMessage) => {
    // Check if there's any debug information to show
    const hasDebugInfo = debugMode && (
      message.instruction ||
      (message.dbRelevantPairs && message.dbRelevantPairs.length > 0) ||
      (message.nonDbRelevantPairs && message.nonDbRelevantPairs.length > 0) ||
      (message.queryResult && message.queryResult.length > 0)
    );

    if (!hasDebugInfo) return null;

    return (
      <Box sx={{ 
        maxWidth: '100%',
        overflow: 'hidden',
        wordBreak: 'break-word'
      }}>
        <CollapsibleSection
          title="Debug Information"
          isExpanded={debugExpanded[message.id] || false}
          onToggle={() => toggleDebugInfo(message.id)}
        >
        {/* AI Instructions */}
        {message.instruction && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom color='blue'>
              AI Instructions
            </Typography>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {message.instruction}
              </Typography>
            </Paper>
          </Box>
        )}

        {/* Database-Sourced Q&A Pairs */}
        {message.dbRelevantPairs && message.dbRelevantPairs.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom color='blue'>
              Database-Sourced Q&A Pairs ({message.dbRelevantPairs.length})
            </Typography>
            {message.dbRelevantPairs.map((pair: any, index: number) => (
              <Paper key={index} sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Question: {truncateText(pair.question)}
                  <Tooltip title="Copy full question to clipboard">
                    <IconButton size="small" onClick={() => copyToClipboard(pair.question)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <b>Related Query:</b> {truncateText(pair.answer)}
                </Box>
                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between' }}>
                  {pair.score && (
                    <Typography variant="caption" color="text.secondary">
                      Relevance Score: {(pair.score * 100).toFixed(1)}%
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {getSourceDisplay(pair)}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>
        )}

        {/* General Reference Q&A Pairs */}
        {message.nonDbRelevantPairs && message.nonDbRelevantPairs.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom color='blue'>
              General Reference Q&A Pairs ({message.nonDbRelevantPairs.length})
            </Typography>
            {message.nonDbRelevantPairs.map((pair: any, index: number) => (
              <Paper key={index} sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Question: {truncateText(pair.question)}
                  <Tooltip title="Copy full question to clipboard">
                    <IconButton size="small" onClick={() => copyToClipboard(pair.question)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <b>Answer:</b> <Box component="span" sx={{ display: 'inline' }}><ReactMarkdown>{truncateText(pair.answer)}</ReactMarkdown></Box>
                </Box>
                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between' }}>
                  {pair.score && (
                    <Typography variant="caption" color="text.secondary">
                      Relevance Score: {(pair.score * 100).toFixed(1)}%
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {getSourceDisplay(pair)}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>
        )}

        {/* Database Query and Results */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom color='blue'>
            Database Query and Results
          </Typography>
          
          {message.queryAttempted ? (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                ({message.queryResult ? message.queryResult.length : 0} total{message.queryResult && message.queryResult.length > 0 ? `, showing ${Math.min(5, message.queryResult.length)}` : ''})
                {message.retryUsed && <Typography component="span" variant="caption" sx={{ ml: 1, color: 'orange' }}>(Retry Used)</Typography>}
              </Typography>
            
            {/* Always show query sequence when queries are attempted */}
            {message.querySequence && message.querySequence.length > 0 ? (
              <Box sx={{ mb: 2 }}>
                {message.querySequence.map((queryInfo, index) => {
                  // Enhanced styling based on query type and results
                  const getQueryStyle = () => {
                    if (queryInfo.type === 'retry' && queryInfo.results !== null && queryInfo.results > 0) return '2px solid #4caf50';
                    if ((queryInfo.type === 'original' || queryInfo.type === 'original_ai') && queryInfo.results !== null && queryInfo.results === 0) return '2px solid #f44336';
                    if (queryInfo.type === 'executed' && queryInfo.results !== null && queryInfo.results > 0) return '2px solid #2196f3';
                    if (queryInfo.type === 'executed' && queryInfo.results !== null && queryInfo.results === 0) return '1px solid #ff9800';
                    return '1px solid #e0e0e0';
                  };
                  
                  const getQueryColor = () => {
                    if (queryInfo.type === 'retry' && queryInfo.results !== null && queryInfo.results > 0) return 'success.main';
                    if ((queryInfo.type === 'original' || queryInfo.type === 'original_ai') && queryInfo.results !== null && queryInfo.results === 0) return 'error';
                    if (queryInfo.type === 'executed' && queryInfo.results !== null && queryInfo.results > 0) return 'primary';
                    if (queryInfo.type === 'executed' && queryInfo.results !== null && queryInfo.results === 0) return 'warning.main';
                    return 'text.primary';
                  };
                  
                  const getQueryLabel = () => {
                    switch (queryInfo.type) {
                      case 'original': return 'Original Query';
                      case 'original_ai': return 'AI Generated Query';
                      case 'retry': return 'Retry Query';
                      case 'executed': return 'Executed Query';
                      case 'single': return 'Database Query';
                      case 'fallback': return 'Query';
                      default: return 'Query';
                    }
                  };
                  
                  const getStatusIcon = () => {
                    if (queryInfo.type === 'retry' && queryInfo.results !== null && queryInfo.results > 0) return <Typography component="span" sx={{ ml: 1, color: 'success.main' }}>✓ Success</Typography>;
                    if ((queryInfo.type === 'original' || queryInfo.type === 'original_ai') && queryInfo.results !== null && queryInfo.results === 0) return <Typography component="span" sx={{ ml: 1, color: 'error.main' }}>✗ No Results</Typography>;
                    if (queryInfo.type === 'executed' && queryInfo.results !== null && queryInfo.results > 0) return <Typography component="span" sx={{ ml: 1, color: 'primary.main' }}>✓ Executed</Typography>;
                    if (queryInfo.type === 'executed' && queryInfo.results !== null && queryInfo.results === 0) return <Typography component="span" sx={{ ml: 1, color: 'warning.main' }}>! No Results</Typography>;
                    if (queryInfo.results !== null && queryInfo.results > 0) return <Typography component="span" sx={{ ml: 1, color: 'success.main' }}>✓</Typography>;
                    if (queryInfo.results !== null && queryInfo.results === 0) return <Typography component="span" sx={{ ml: 1, color: 'warning.main' }}>!</Typography>;
                    return null;
                  };
                  
                  return (
                    <Paper key={index} sx={{ p: 2, mb: 2, border: getQueryStyle() }}>
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="subtitle2" color={getQueryColor()} sx={{ display: 'inline' }}>
                          {getQueryLabel()}
                          {queryInfo.results !== null && (
                            <Typography component="span" sx={{ ml: 1, fontWeight: 'normal' }}>
                              ({queryInfo.results} result{queryInfo.results !== 1 ? 's' : ''})
                            </Typography>
                          )}
                          {getStatusIcon()}
                        </Typography>
                        {queryInfo.description && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {queryInfo.description}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ 
                        whiteSpace: 'pre-wrap', 
                        fontFamily: 'monospace', 
                        backgroundColor: '#f5f5f5', 
                        p: 1, 
                        borderRadius: 1,
                        wordBreak: 'break-all',
                        overflowWrap: 'anywhere',
                        maxWidth: '100%'
                      }}>
                        {queryInfo.query}
                      </Typography>
                    </Paper>
                  );
                })}
              </Box>
            ) : message.queryAttempted ? (
              // Fallback for when queryAttempted is true but no querySequence
              <Paper sx={{ p: 2, mb: 2, border: '1px solid #ff9800' }}>
                <Typography variant="subtitle2" color="warning.main" sx={{ mb: 1 }}>
                  Database Query (Fallback Display)
                  <Typography component="span" sx={{ ml: 1, color: 'warning.main' }}>! No Sequence Data</Typography>
                </Typography>
                <Typography variant="body2" sx={{ 
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                  backgroundColor: '#f5f5f5',
                  p: 1,
                  borderRadius: 1,
                  wordBreak: 'break-all',
                  overflowWrap: 'anywhere',
                  maxWidth: '100%'
                }}>
                  {message.queryToRun || 'Query information unavailable'}
                </Typography>
              </Paper>
            ) : null}
            {/* Show results section only when there are actual results */}
            {message.queryResult && message.queryResult.length > 0 ? (
              (() => {
                const isExpanded = tableExpanded[message.id] || false;
                const allColumns = message.queryResult[0] ? Object.keys(message.queryResult[0]) : [];
                const importantColumns = ['id', 'name', 'title', 'description', 'short_description', 'price', 'status'];
                const displayColumns = isExpanded ? allColumns : allColumns.filter(col => 
                  importantColumns.some(important => col.toLowerCase().includes(important.toLowerCase()))
                ).slice(0, 6);
                
                if (displayColumns.length === 0 && !isExpanded) {
                  // Fallback to first 5 columns if no important ones found
                  displayColumns.push(...allColumns.slice(0, 5));
                }


                return (
                  <Paper sx={{ mb: 2 }}>
                    {/* Table controls */}
                    <Box sx={{ p: 2, pb: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {isExpanded ? `All ${allColumns.length} columns` : `${displayColumns.length} key columns`}
                      </Typography>
                      <Button 
                        size="small" 
                        onClick={() => toggleTableExpansion(message.id)}
                        sx={{ minWidth: 'auto' }}
                      >
                        {isExpanded ? 'Show Less' : `Show All (${allColumns.length} cols)`}
                      </Button>
                    </Box>
                    
                    {/* Material-UI TableContainer with proper scrolling */}
                    <TableContainer 
                      component={Paper}
                      sx={{ 
                        height: '400px', // Fixed height like an embed
                        maxHeight: '400px',
                        overflowY: 'auto', // Vertical scrolling within table
                        overflowX: 'auto', // Horizontal scrolling within table
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            {displayColumns.map((key, index) => (
                              <TableCell 
                                key={index}
                                sx={{ 
                                  minWidth: isExpanded ? '120px' : '100px', // Reasonable minimum for 71 columns
                                  maxWidth: isExpanded ? '250px' : '150px', // Prevent excessive column width
                                  backgroundColor: '#f5f5f5',
                                  fontWeight: 'bold'
                                }}
                              >
                                {key}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {message.queryResult.slice(0, 5).map((row, rowIndex) => (
                            <TableRow key={rowIndex}>
                              {displayColumns.map((key, valIndex) => {
                                const value = row[key];
                                const cellContent = isExpanded ? 
                                  (typeof value === 'object' ? 
                                    JSON.stringify(value) : 
                                    typeof value === 'string' && (value.startsWith('<') || value.includes('</')) ? 
                                      value.replace(/<[^>]*>/g, '') : // Strip HTML for expanded view
                                      String(value || '')
                                  ) :
                                  truncateCellContent(value, 100);
                                
                                return (
                                  <TableCell 
                                    key={valIndex}
                                    sx={{ 
                                      minWidth: isExpanded ? '120px' : '100px', // Consistent with header min width
                                      maxWidth: isExpanded ? '250px' : '150px', // Consistent with header max width
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: isExpanded ? 'normal' : 'nowrap',
                                      wordBreak: 'break-word', // Break long words
                                      // 5-line text display for expanded mode (table-compatible)
                                      ...(isExpanded && {
                                        lineHeight: '1.4',
                                        maxHeight: '7em', // 5 lines × 1.4 line-height = 7em
                                      })
                                    }}
                                  >
                                    {cellContent}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    {/* Footer info */}
                    <Box sx={{ px: 2, pb: 2 }}>
                      {message.queryResult.length > 5 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          ... and {message.queryResult.length - 5} more rows (truncated for display)
                        </Typography>
                      )}
                      {!isExpanded && allColumns.length > displayColumns.length && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Hidden columns: {allColumns.filter(col => !displayColumns.includes(col)).join(', ')}
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                );
              })()
            ) : (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Query executed successfully - No results found
                </Typography>
              </Paper>
            )}
            </Box>
          ) : (
            <Paper sx={{ p: 2, mb: 2, bgcolor: '#f9f9f9' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No database query was used for this response
              </Typography>
            </Paper>
          )}
        </Box>
        </CollapsibleSection>
      </Box>
    );
  };

  return (
    <Box sx={{ 
      maxWidth: '100%',
      overflow: 'hidden', // Prevent horizontal scroll on the main container
      width: '100%'
    }}>
      <Typography variant="h4" gutterBottom>
        Test Chatbot
      </Typography>
      
      {aiTitle && versionName && (
        <Typography variant="subtitle1" gutterBottom>
          Testing {aiTitle} - Version: {versionName}
        </Typography>
      )}
      
      {/* Debug: Show current userID */}
      <Typography variant="subtitle2" gutterBottom sx={{ 
        color: 'orange', 
        fontFamily: 'monospace', 
        backgroundColor: '#fff3e0', 
        padding: '4px 8px', 
        borderRadius: 1,
        display: 'inline-block',
        fontSize: '0.85rem'
      }}>
        🐛 Debug UserID: test-{sessionId}
      </Typography>
      
      
      {/* Conversation Branching Explanation */}
      <Box sx={{ mb: 3, p: 2, bgcolor: '#e8f5e9', borderRadius: 1, borderLeft: '4px solid #4caf50' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          <strong>💡 Conversation Branching:</strong> This test page supports "time travel" - you can create new conversation branches by typing a new question after any previous response. 
          This creates a new path from that point forward, allowing you to test different conversation flows and see how the AI responds differently.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Each input field below messages lets you start a new branch from that point in the conversation.
        </Typography>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Initial input section */}
      <InputSection 
        questionValue={currentQuestion}
        onQuestionChange={setCurrentQuestion}
        onSubmit={() => handleTest()}
        isLoading={loading}
        placeholder="Ask a question"
      />
      
      {/* Conversation messages */}
      {messages.map((message, index) => (
        <Box key={message.id} sx={{ 
          mb: 6,
          maxWidth: '100%',
          overflow: 'hidden',
          wordBreak: 'break-word'
        }}>
          {/* Divider between messages */}
          {index > 0 && <Divider sx={{ mb: 4 }} />}
          
          {/* Question */}
          <Typography variant="h6" gutterBottom sx={{ color: '#1976d2' }}>
            Question {index + 1}:
          </Typography>
          <Paper sx={{ 
            p: 2, 
            mb: 2, 
            bgcolor: '#f5f5f5',
            maxWidth: '100%',
            overflow: 'hidden',
            wordBreak: 'break-word'
          }}>
            <Typography variant="body1">
              {message.question}
            </Typography>
          </Paper>
          
          {/* Response */}
          <Typography variant="h6" gutterBottom sx={{ color: '#2e7d32' }}>
            Response:
          </Typography>
          <Box sx={{ 
            p: 2, 
            bgcolor: '#081622', 
            color: 'white', 
            borderRadius: 1, 
            mb: 2,
            maxWidth: '100%',
            overflow: 'hidden',
            wordBreak: 'break-word'
          }}>
            <Box component="div" sx={{ '& p': { margin: 0 }, '& p:not(:last-child)': { marginBottom: 1 } }}>
              <ReactMarkdown>{message.response}</ReactMarkdown>
            </Box>
          </Box>
          
          {/* Q&A Pairs and debug info */}
          {renderQAPairs(message)}
          
          {/* Input for next question */}
          <InputSection 
            ref={(el) => {
              console.log('Setting ref for message:', message.id, 'element:', el);
              messageInputRefs.current[message.id] = el;
            }}
            questionValue={additionalQuestions[message.id] || ''}
            onQuestionChange={(value) => handleAdditionalQuestionChange(message.id, value)}
            onSubmit={() => {
              console.log('InputSection onSubmit triggered for message:', message.id);
              handleAdditionalQuestionSubmit(message.id);
            }}
            isLoading={loading}
            placeholder="Ask another question"
          />
        </Box>
      ))}
    </Box>
  );
};

export default TestChatbot;
